import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    DividendDistributionRequest,
    GetDividendDeclarationResponse,
    GetDividendDeclarationsResponse,
    GetShareholdersResponse,
} from '@/api/handlers/resources/dividends';
import type { CalculateDistributionResponse } from '@/api/handlers/resources/dividends';
import type { ApiResponse } from '@/api/handlers/responses';
import { DividendQueryKeys } from './dividendQueryKeys';

const STALE_TIME_MS = 60_000;

/**
 * The shareholder register and the distributable-profit summary, as of a date.
 * `keepPreviousData` holds the current rows while a date change loads, so the
 * summary and the tie-out don't blink between two valid states.
 */
export const useShareholdersQuery = (asOf: string, enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetShareholdersResponse>>({
        queryKey: DividendQueryKeys.shareholders(asOf),
        queryFn: () => apiClient.dividends.getShareholders(asOf),
        enabled: enabled && asOf.length > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};

/** Committed declarations, newest first. A preview is not a declaration. */
export const useDividendDeclarationsQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetDividendDeclarationsResponse>>({
        queryKey: DividendQueryKeys.declarations(),
        queryFn: () => apiClient.dividends.getDeclarations(),
        enabled,
        staleTime: STALE_TIME_MS,
    });
};

/**
 * One declaration's breakdown, by the id of the **entry** it created — a
 * declaration has no id of its own. Disabled while the drawer is closed.
 */
export const useDividendDeclarationQuery = (journalEntryId: string | null) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetDividendDeclarationResponse>>({
        queryKey: DividendQueryKeys.declaration(journalEntryId ?? 'none'),
        queryFn: () =>
            apiClient.dividends.getDeclaration(journalEntryId as string),
        enabled: journalEntryId !== null,
    });
};

/**
 * The extraction calculator.
 *
 * A **query**, not a mutation: it is a pure read that refetches as the user types,
 * and keying it on the inputs means re-entering the same figure is answered from
 * cache rather than re-round-tripped. `keepPreviousData` keeps the previous
 * answer on screen while the next one loads, so the figures don't flash to empty
 * on every keystroke.
 */
export const useDistributionQuoteQuery = (
    body: DividendDistributionRequest,
    asOf: string,
    enabled: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<CalculateDistributionResponse>>({
        queryKey: DividendQueryKeys.quote(body.direction, body.amount, asOf),
        queryFn: () => apiClient.dividends.calculateDistribution(body, asOf),
        enabled: enabled && asOf.length > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};
